import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { colors } from '@guryou/colors';
import { Block, Form } from '@guryou/html-components';
import { generatePaymentIntent, useEffectOnSuccess } from '@guryou/redux';
import { printPrice } from '@guryou/utils';
import { isValidEmail } from '@guryou/utils/build/stringUtils';
import WidgetContactForm from './WidgetContactForm';
import { WidgetContext } from './WidgetContext';

const WidgetMembershipDetail = ({ membership, openedMembershipId, setOpenedMembershipId }) => {
  const { fontF, fontS, paymentOptions } = useContext(WidgetContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);

  const membershipOnline = paymentOptions?.data?.membershipOnline;
  const membershipOnsite = paymentOptions?.data?.membershipOnsite;

  useEffectOnSuccess([state => state.payments.paymentIntent], () => {
    setOpenedMembershipId(null);
  });

  if (!membership) {
    return null;
  }

  const onSubmit = data => {
    if (!isValidEmail(data.email)) {
      return;
    }
    dispatch(
      generatePaymentIntent({
        email: data.email,
        firstname: data.firstname,
        lastname: data.lastname,
        membershipId: openedMembershipId,
        phone: data.phone,
      })
    );
  };

  const closeModal = () => {
    setOpenedMembershipId(null);
    setOpen(false);
  };

  const renderModal = () => {
    if (!open) {
      return null;
    }

    return (
      <div class="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-10 justify-center items-center w-full md:inset-0 max-h-full bg-black bg-opacity-50">
        <div class="relative p-4 w-full max-w-[720px]">
          <div class="relative bg-white rounded-lg shadow ">
            <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
              <h3 class="text-lg font-semibold text-gray-900">{membership?.name}</h3>
              <button
                onClick={closeModal}
                type="button"
                class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              >
                <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                </svg>
                <span class="sr-only">Close modal</span>
              </button>
            </div>

            <div class="p-4 md:p-5">
              <ol class="relative text-gray-500 border-s border-gray-200 ml-4 pt-6">
                <li class="mb-10 ms-6">
                  <span
                    class={`absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:bg-[${colors.mandy}]`}
                  >
                    <svg
                      class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 16"
                    >
                      <path d="M18 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM6.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.014 13.021l.157-.625A3.427 3.427 0 0 1 6.5 9.571a3.426 3.426 0 0 1 3.322 2.805l.159.622-6.967.023ZM16 12h-3a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Z" />
                    </svg>
                  </span>
                  <h3 class="font-medium leading-tight">{t('SERVICES.personalData')}</h3>
                  <p class="text-sm">{t('SERVICES.personalDataStepDescription')}</p>

                  <div class="mt-6 grow sm:mt-8 lg:mt-4">
                    <WidgetContactForm />
                  </div>
                </li>
              </ol>
            </div>
            <div class="flex justify-end mt-6 gap-2 p-4">
              <button
                type="submit"
                style={{ backgroundColor: colors.mandy }}
                class="max-w-[180px] text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-red-100 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
              >
                {`${t('GENERAL.pay')} ${printPrice(membership.price)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Block overflow="hidden">
      <Form parserVersion={0} onSubmit={onSubmit} actions={false} overflow="hidden">
        <div class="w-full max-w-sm p-4 bg-white border border-gray-200 rounded-lg shadow sm:p-8">
          <h5 class="text-xl font-medium trancking-tight font-semibold max-w-md truncate">{membership?.name}</h5>
          <p class="mb-5 font-normal text-gray-500">{membership?.description}</p>
          <div class="flex items-baseline text-gray-900">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 16 16">
              <circle cx="8" cy="8" r="8" fill="currentColor" class="text-gray-300" />
              <circle cx="8" cy="8" r="3" fill="currentColor" class="text-gray-500" />
            </svg>
            <span class="text-3xl font-semibold ml-2">
              <span class="ms-1 text-xl font-normal text-gray-500">{t('GENERAL.price')}: </span>
            </span>
            <span class="ms-1 text-xl font-normal text-gray-500 trancking-tight">
              {membership?.price}
              <span class="text-xl "> €</span>
            </span>
          </div>
          <div class="flex items-baseline text-gray-900">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 16 16">
              <circle cx="8" cy="8" r="8" fill="currentColor" class="text-gray-300" />
              <circle cx="8" cy="8" r="3" fill="currentColor" class="text-gray-500" />
            </svg>
            <span class="text-3xl font-semibold ml-2">
              <span class="ms-1 text-xl font-normal text-gray-500">
                {t('MEMBERSHIPS.numberOfSessions')}: {membership.sessionsCounter || '0'}
              </span>
            </span>
          </div>
          <ul role="list" class="space-y-5 my-7"></ul>
          {(membershipOnline || membershipOnsite) && (
            <button
              onClick={() => {
                setOpen(true);
                setOpenedMembershipId(membership.id);
              }}
              type="button"
              style={{ backgroundColor: colors.mandy }}
              class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-red-100 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
            >
              {t('GENERAL.payNow')}
            </button>
          )}
        </div>
        {renderModal()}
      </Form>
    </Block>
  );
};

export default WidgetMembershipDetail;
